import React, { useState, useEffect } from 'react';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';

/**
 * StatChecker Component v1.7
 * FLEXIBLE LAYOUT EDITION - Optimized for Ultrawide monitors.
 * FULL PDF AUDIT - Comprehensive coverage of Sheet 2 paths.
 */
const StatChecker = () => {
  const db = getFirestore();

  // Config State (Persisted to localStorage)
  const [config, setConfig] = useState(() => {
    const saved = localStorage.getItem('mgg_stat_checker_config');
    return saved ? JSON.parse(saved) : {
      userId: '',
      groupId: '',
      gameId: '',
      gameName: '',
      year: new Date().getFullYear().toString()
    };
  });

  useEffect(() => {
    localStorage.setItem('mgg_stat_checker_config', JSON.stringify(config));
  }, [config]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="section py-4">
      <div className="indent-from-top is-fluid">
        {/* Header & Controls - Flexible row for inputs */}
        <div className="box has-background-dark has-text-white mb-5 p-4">
          <div className="level mb-4">
            <div className="level-left">
              <div className="level-item">
                <div>
                  <h1 className="title is-5 has-text-white mb-0 italic tracking-tighter">
                    STAT CHECKER <span className="tag is-info is-rounded ml-1">v1.7</span>
                  </h1>
                  <p className="is-size-7 has-text-grey-light uppercase tracking-widest font-bold">Ultrawide Optimized • Full PDF Audit</p>
                </div>
              </div>
            </div>
            <div className="level-right">
              <div className="level-item">
                <span className="tag is-success is-light is-small font-bold">LIVE SYNC</span>
              </div>
            </div>
          </div>

          <div className="columns is-multiline is-mobile">
            {[
              { label: 'User ID', name: 'userId' },
              { label: 'Group ID', name: 'groupId' },
              { label: 'Game ID (Doc)', name: 'gameId' },
              { label: 'Game Name (Stat)', name: 'gameName' },
              { label: 'Year', name: 'year' }
            ].map(input => (
              <div key={input.name} className="column is-narrow-desktop is-half-tablet is-full-mobile" style={{ minWidth: '200px' }}>
                <div className="field">
                  <label className="label is-size-7 has-text-grey-light uppercase mb-1">{input.label}</label>
                  <div className="control">
                    <input 
                      type="text"
                      name={input.name}
                      value={config[input.name]}
                      onChange={handleInputChange}
                      className="input is-small is-family-monospace has-background-black-ter has-text-info is-borderless shadow-inner"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Audit Grid - Standard Bulma Columns for full width flexibility */}
        <div className="columns is-multiline is-variable is-2">
          
          {/* USER: COMPETITIVE */}
          <div className="column is-one-third-widescreen is-half-desktop is-full-tablet">
            <StatCard 
              title="User: Competitive Stats"
              path={`users/${config.userId}/gameStats/${config.gameName}`}
              db={db}
              fields={[
                { key: 'totalWins', label: 'Total Wins', type: 'number' },
                { key: 'totalScore', label: 'Total Score', type: 'number' },
                { key: 'personalBest', label: 'Personal Best', type: 'number' },
                { key: 'personalBestDate', label: 'PB Date', type: 'string' },
                { key: 'personalWorst', label: 'Personal Worst', type: 'number' },
                { key: 'personalWorstDate', label: 'PW Date', type: 'string' }
              ]}
            />
          </div>

          {/* USER: COOPERATIVE */}
          <div className="column is-one-third-widescreen is-half-desktop is-full-tablet">
            <StatCard 
              title="User: Cooperative Stats"
              path={`users/${config.userId}/gameStats/${config.gameName}`}
              db={db}
              fields={[
                { key: 'totalCooperativeWins', label: 'Co-op Wins', type: 'number' },
                { key: 'totalCooperativeScore', label: 'Total Co-op Score', type: 'number' },
                { key: 'personalCooperativeBest', label: 'Co-op PB', type: 'number' },
                { key: 'personalCooperativeBestDate', label: 'Co-op PB Date', type: 'string' },
                { key: 'personalCooperativeWorst', label: 'Co-op Worst', type: 'number' },
                { key: 'personalCooperativeWorstDate', label: 'Co-op Worst Date', type: 'string' }
              ]}
            />
          </div>

          {/* USER: YEARLY & LIFETIME SUMMARY */}
          <div className="column is-one-third-widescreen is-half-desktop is-full-tablet">
            <div className="columns is-multiline is-gapless">
                <div className="column is-full mb-2">
                    <StatCard 
                    title={`User: Year (${config.year})`}
                    path={`users/${config.userId}/stats/all-time/years/${config.year}`}
                    db={db}
                    fields={[
                        { key: 'totalWins', label: 'Yearly Wins', type: 'number' },
                        { key: 'totalPlays', label: 'Yearly Plays', type: 'number' },
                        { key: 'gameWins', label: 'Yearly Unique Wins', type: 'number' },
                        { key: 'gamePlays', label: 'Yearly Unique Plays', type: 'number' }
                    ]}
                    />
                </div>
                <div className="column is-full">
                    <StatCard 
                    title="User: Lifetime Global"
                    path={`users/${config.userId}/stats/all-time`}
                    db={db}
                    fields={[
                        { key: 'totalWins', label: 'Lifetime Wins', type: 'number' },
                        { key: 'totalPlays', label: 'Lifetime Plays', type: 'number' },
                        { key: 'gameWins', label: 'Unique Game Wins', type: 'number' },
                        { key: 'gamePlays', label: 'Unique Game Plays', type: 'number' }
                    ]}
                    />
                </div>
            </div>
          </div>

          {/* GROUP: LIFETIME GAME RECORDS */}
          <div className="column is-one-third-widescreen is-half-desktop is-full-tablet">
            <StatCard 
              title="Group: Lifetime Game Records"
              path={`groups/${config.groupId}/games/${config.gameId}`}
              db={db}
              fields={[
                { key: 'playerHighscores', label: 'High Scores', type: 'map' },
                { key: 'playerLowScores', label: 'Low Scores', type: 'map' },
                { key: 'bestLosingScore', label: 'Heartbreak PB', type: 'number' },
                { key: 'bestLosingScoreDate', label: 'Heartbreak Date', type: 'string' },
                { key: 'biggestSpread', label: 'Biggest Spread', type: 'number' },
                { key: 'worstWinningScore', label: 'Ugly Win', type: 'number' },
                { key: 'worstWinningScoreDate', label: 'Ugly Win Date', type: 'string' },
                { key: 'totalMarginOfVictory', label: 'Total Margin', type: 'number' }
              ]}
            />
          </div>

          {/* GROUP: YEARLY GAME RECORDS */}
          <div className="column is-one-third-widescreen is-half-desktop is-full-tablet">
            <StatCard 
              title={`Group: ${config.year} Game Records`}
              path={`groups/${config.groupId}/games/${config.gameId}/statsByYear/${config.year}`}
              db={db}
              fields={[
                { key: 'biggestSpread', label: 'Year Spread', type: 'number' },
                { key: 'biggestSpreadDate', label: 'Year Spread Date', type: 'string' },
                { key: 'worstWinningScore', label: 'Year Ugly Win', type: 'number' },
                { key: 'worstWinningScoreDate', label: 'Year Ugly Date', type: 'string' },
                { key: 'worstCooperativeWinningScore', label: 'Worst Co-op Win', type: 'number' },
                { key: 'worstCooperativeWinningScoreDate', label: 'Worst Co-op Date', type: 'string' },
                { key: 'totalMarginOfVictory', label: 'Year Margin', type: 'number' },
                { key: 'playerHighscores', label: 'Year High Scores', type: 'map' },
                { key: 'playerLowScores', label: 'Year Low Scores', type: 'map' },
                { key: 'playerCooperativeHighscores', label: 'Year Co-op Highs', type: 'map' },
                { key: 'playerCooperativeLowScores', label: 'Year Co-op Lows', type: 'map' }
              ]}
            />
          </div>

          {/* GROUP: TOTALS & SUMMARY */}
          <div className="column is-one-third-widescreen is-half-desktop is-full-tablet">
            <div className="columns is-multiline is-gapless">
                <div className="column is-full mb-2">
                    <StatCard 
                    title={`Group: Summary (${config.year})`}
                    path={`groups/${config.groupId}/statsByYear/${config.year}`}
                    db={db}
                    fields={[
                        { key: 'winners', label: 'Yearly Winners', type: 'map' },
                        { key: 'memberPlayCount', label: 'Yearly Plays', type: 'map' },
                        { key: `gamesPlayed[${config.gameId}]`, label: 'Yearly Game Tally', type: 'map' }
                    ]}
                    />
                </div>
                <div className="column is-full">
                    <StatCard 
                    title="Group: Global Lifetime"
                    path={`groups/${config.groupId}`}
                    db={db}
                    fields={[
                        { key: 'winners', label: 'Lifetime Winners', type: 'map' },
                        { key: 'memberPlayCount', label: 'Lifetime Plays', type: 'map' }
                    ]}
                    />
                </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const StatCard = ({ title, path, fields, db }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path || path.includes('//') || path.split('/').some(p => !p)) {
        setLoading(false);
        return;
    }

    setLoading(true);
    const unsubscribe = onSnapshot(doc(db, path), 
      (snapshot) => {
        setData(snapshot.exists() ? snapshot.data() : {});
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [path, db]);

  const hasData = data && Object.keys(data).length > 0;

  return (
    <div className="card is-shadowless border-slate-700 has-background-dark-soft mb-2 h-full">
      <header className="card-header has-background-grey-darker py-1">
        <p className="card-header-title is-size-7 has-text-grey-lighter uppercase py-1 px-3">
          {title}
        </p>
        <div className="card-header-icon py-1 px-3">
          {loading ? (
             <div className="loader is-size-7"></div>
          ) : (
             <span className={`tag is-rounded is-small font-bold px-2 ${hasData ? 'is-success' : 'is-dark'}`} >
                {hasData ? 'SYNC' : 'NULL'}
             </span>
          )}
        </div>
      </header>
      
      <div className="is-family-monospace has-background-black-bis has-text-light is-size-6 p-1 px-3" style={{ whiteSpace: 'nowrap', overflowX: 'auto', borderBottom: '1px solid #2d3748' }}>
        {path}
      </div>

      <div className="card-content p-0">
        {error ? (
          <div className="p-3 has-background-danger-dark has-text-danger-light is-size-6 font-bold">
            <span className="icon mr-1"><i className="fas fa-exclamation-triangle"></i></span>
            {error}
          </div>
        ) : (!hasData && !loading) ? (
          <div className="p-4 has-text-centered has-text-grey-dark is-size-6 italic">
            Document Empty
          </div>
        ) : (
          <table className="table is-fullwidth is-narrow is-hoverable has-background-transparent m-0">
            <tbody>
              {fields.map(field => (
                <tr key={field.key} className="is-size-6 border-slate-800">
                  <td className="has-text-grey pl-3 py-1" style={{ width: '50%', borderBottom: '1px solid #1a202c' }}>
                    <span className="is-block has-text-grey-darker has-text-weight-bold">{field.label}</span>
                    <span className="is-family-monospace has-text-grey-dark" style={{fontSize: '0.75rem'}}>{field.key}</span>
                  </td>
                  <td className="has-text-right pr-3 py-1" style={{ borderBottom: '1px solid #1a202c' }}>
                    <ValueDisplay value={data?.[field.key]} type={field.type} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const ValueDisplay = ({ value, type }) => {
  if (value === undefined || value === null) {
    return <span className="has-text-grey-dark">--</span>;
  }

  if (type === 'map' || type === 'array' || typeof value === 'object') {
    return (
        <pre className="p-1 has-background-black-bis has-text-info-light text-left overflow-auto" style={{ maxHeight: '160px', border: '1px solid #2d3748' }}>
            {JSON.stringify(value, null, 1)}
        </pre>
    );
  }

  if (typeof value === 'number') {
    return <span className="is-size-6 has-text-weight-bold has-text-success">{value}</span>;
  }

  return <span className="is-size-6 has-text-dark has-text-weight-semibold">{value.toString()}</span>;
};

export default StatChecker;